import React, { useState } from 'react';

interface FinalConfirmationProps {
  form: {
    roleName: string;
    roleCategory: string;
    roleDescription: string;
    template: string;
  };
  selectedPermissions: Record<string, string[]>;
  accessLevel: 'full' | 'restricted' | 'readonly';
  settings: {
    mfa: boolean;
    autoTimeout: boolean;
    ipRestriction: boolean;
    auditLogging: boolean;
    timeRestriction: boolean;
  };
  maxUsers: number | '';
  sessionDuration: string;
  isConfirmed: boolean; // Receive isConfirmed prop
  setIsConfirmed: (value: boolean) => void; // Function to update isConfirmed state
}

const FinalConfirmation: React.FC<FinalConfirmationProps> = ({
  form,
  selectedPermissions,
  accessLevel,
  settings,
  maxUsers,
  sessionDuration,
  isConfirmed,
  setIsConfirmed,
}) => {
  const permissionsList = Object.keys(selectedPermissions).map((module) => (
    <li key={module}>
      <strong>{module}:</strong> {selectedPermissions[module].join(', ')}
    </li>
  ));

  return (
    <div className="rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-700">Review & Create</h2>
        <p className="text-sm text-gray-500">
          Review all information before creating the admin user
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Section */}
        <div>
          <h3 className="font-medium text-gray-600 mb-2">Role Information</h3>
          <div className="text-sm text-gray-700 space-y-1">
            <p>
              <strong>Role Name:</strong> {form.roleName}
            </p>
            <p>
              <strong>Role Category:</strong> {form.roleCategory}
            </p>
            <p>
              <strong>Role Description:</strong> {form.roleDescription}
            </p>
            <p>
              <strong>Template:</strong> {form.template}
            </p>
          </div>

          <h3 className="font-medium text-gray-600 mt-6 mb-2">Security Preferences</h3>
          <ul className="list-disc list-inside text-sm text-gray-700">
            <li>{settings.mfa ? 'Require Multi-Factor Authentication' : 'No MFA'}</li>
            <li>{settings.autoTimeout ? 'Enable Automatic Timeout' : 'No Auto Timeout'}</li>
            <li>{settings.ipRestriction ? 'Enable IP Restrictions' : 'No IP Restrictions'}</li>
            <li>{settings.auditLogging ? 'Enable Audit Logging' : 'No Audit Logging'}</li>
            <li>
              {settings.timeRestriction ? 'Enable Time Restrictions' : 'No Time Restrictions'}
            </li>
          </ul>
        </div>

        {/* Right Section */}
        <div>
          <h3 className="font-medium text-gray-600 mb-2">Role & Permissions</h3>
          <ul className="list-disc list-inside text-sm text-gray-700">{permissionsList}</ul>

          <h3 className="font-medium text-gray-600 mt-6 mb-2">Access Level</h3>
          <p className="text-sm text-gray-700">{accessLevel}</p>

          <h3 className="font-medium text-gray-600 mt-6 mb-2">Limits</h3>
          <p className="text-sm text-gray-700">
            <strong>Max Users:</strong> {maxUsers || 'Unlimited'}
          </p>
          <p className="text-sm text-gray-700">
            <strong>Session Duration:</strong> {sessionDuration} minutes
          </p>
        </div>
      </div>

      <div className="mt-6">
        <label className="inline-flex items-center text-sm text-gray-700">
          <input
            type="checkbox"
            checked={isConfirmed}
            onChange={(e) => setIsConfirmed(e.target.checked)}
            className="form-checkbox h-4 w-4 text-green-600"
          />
          <span className="ml-2">
            I confirm that all the information provided is accurate and I want to create this admin
            user.
          </span>
        </label>
      </div>
    </div>
  );
};

export default FinalConfirmation;
